import { useParams } from "react-router-dom";
import {
  fetchTemplateById,
  deleteTemplate,
  updateTemplate,
  createTemplate,
} from "../../redux/slices/templates";
import React, { useEffect, useState } from "react";
import { NavBar, SideNav } from "../../components";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import TaskDetails from "../ViewApplication/TaskDetails";
import {
  AiFillPlusCircle,
  AiOutlineDelete,
  AiOutlineEdit,
  AiOutlineCopy,
} from "react-icons/ai";
import EditTaskModal from "../ViewApplication/EditTaskModal";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import EditTemplateMetaModal from "./EditTemplateMetaModal";
import { timestampToTimeAgo } from "../../config/firebase";

const SkeletonLoader = () => (
  <div className="space-y-6">
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700 animate-pulse">
      <div className="h-6 bg-gray-200 dark:bg-gray-600 rounded w-1/3 mb-2"></div>
      <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-2/3 mb-1"></div>
      <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-1/4 mb-4"></div>
      <div className="h-4 bg-red-300 dark:bg-red-700 rounded w-24"></div>
    </div>
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg border border-gray-200 dark:border-gray-700 animate-pulse">
      <div className="h-5 bg-gray-200 dark:bg-gray-600 rounded w-24 mb-4"></div>
      {[...Array(3)].map((_, idx) => (
        <div key={idx} className="mb-4">
          <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-1/2 mb-1"></div>
          <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-1/3"></div>
        </div>
      ))}
    </div>
  </div>
);

const ViewTemplate = () => {
  const dispatch = useDispatch();
  const templatesStore = useSelector((state) => state.templates);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const navigate = useNavigate();
  const { actionLoading } = templatesStore;
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [isEditMetaModalOpen, setEditMetaModalOpen] = useState(false);
  const [isDeleteTemplateModalOpen, setIsDeleteTemplateModalOpen] =
    useState(false);

  const { id } = useParams();

  useEffect(() => {
    if (id) {
      dispatch(fetchTemplateById(id));
    }
  }, [dispatch, id]);

  const handleCreateTaskClicked = () => {
    setSelectedTask({});
    setEditModalOpen(true);
  };

  const handleEditTaskClicked = (task) => {
    setSelectedTask(task);
    setEditModalOpen(true);
  };

  const handleDeleteTaskClicked = (task) => {
    setTaskToDelete(task);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDeleteTask = async () => {
    if (!taskToDelete) return;
    try {
      setIsDeleting(true);
      const filteredTasks = (
        templatesStore.selectedTemplate?.tasks || []
      ).filter((t) => t.id !== taskToDelete.id);

      const updatedTemplate = {
        name: templatesStore.selectedTemplate.name,
        description: templatesStore.selectedTemplate.description,
        tasks: filteredTasks,
      };

      await dispatch(
        updateTemplate({
          id: templatesStore.selectedTemplate.id,
          data: updatedTemplate,
        })
      );

      setIsDeleteModalOpen(false);
      setTaskToDelete(null);
    } catch (error) {
      console.error("Failed to delete task:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleConfirmDeleteTemplate = async () => {
    try {
      setIsDeleting(true);
      await dispatch(deleteTemplate(templatesStore.selectedTemplate.id));
      navigate("/templates");
    } catch (error) {
      console.error("Failed to delete template:", error);
    } finally {
      setIsDeleting(false);
      setIsDeleteTemplateModalOpen(false);
    }
  };

  const handleSaveTask = async (task) => {
    try {
      const sanitizedTasks = (templatesStore.selectedTemplate?.tasks || [])
        .filter((t) => t.id !== task.id)
        .map(
          ({
            id,
            title,
            description,
            type,
            deadline,
            comment,
            attachment,
          }) => ({
            id,
            title,
            description,
            type,
            comment,
            attachment,
            ...(deadline ? { deadline } : {}),
          })
        );

      const newTask = {
        id: task.id || crypto.randomUUID(),
        title: task.title,
        description: task.description,
        type: task.type,
        comment: task.comment,
        attachment: task.attachment,
        ...(task.deadline ? { deadline: task.deadline } : {}),
      };

      const updatedTemplate = {
        name: templatesStore.selectedTemplate.name,
        description: templatesStore.selectedTemplate.description,
        tasks: [newTask, ...sanitizedTasks],
      };

      await dispatch(
        updateTemplate({
          id: templatesStore.selectedTemplate.id,
          data: updatedTemplate,
        })
      );

      setEditModalOpen(false);
    } catch (error) {
      console.error("Failed to save task:", error);
    }
  };

  const handleCloneTemplate = async () => {
    try {
      const { name, description, tasks } = templatesStore.selectedTemplate;
      const resultAction = await dispatch(
        createTemplate({
          name: `${name} (Cloned)`,
          description,
          tasks,
        })
      );

      const newTemplateId = resultAction.payload?.id;
      if (newTemplateId) {
        navigate(`/templates/${newTemplateId}`);
      }
    } catch (error) {
      console.error("Failed to clone template:", error);
    } finally {
    }
  };

  const tasks = templatesStore.selectedTemplate?.tasks || [];

  return (
    <div className="antialiased bg-gray-50 dark:bg-gray-900 min-h-screen">
      <NavBar />
      <SideNav />
      <EditTaskModal
        task={selectedTask}
        isOpen={isEditModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSave={handleSaveTask}
        isLoading={actionLoading}
        isTemplate
      />
      {isEditMetaModalOpen && (
        <EditTemplateMetaModal
          isOpen={isEditMetaModalOpen}
          onClose={() => setEditMetaModalOpen(false)}
          onSave={({ name, description }) =>
            dispatch(
              updateTemplate({
                id: templatesStore.selectedTemplate.id,
                data: {
                  name,
                  description,
                  tasks: templatesStore.selectedTemplate.tasks,
                },
              })
            ).then(() => setEditMetaModalOpen(false))
          }
          name={templatesStore.selectedTemplate?.name}
          description={templatesStore.selectedTemplate?.description}
        />
      )}
      <ConfirmDeleteModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onDelete={handleConfirmDeleteTask}
        isLoading={isDeleting}
        taskTitle={taskToDelete?.title}
      />
      <ConfirmDeleteModal
        open={isDeleteTemplateModalOpen}
        onClose={() => setIsDeleteTemplateModalOpen(false)}
        onDelete={handleConfirmDeleteTemplate}
        isLoading={isDeleting}
        taskTitle={templatesStore.selectedTemplate?.name}
      />
      <main className="p-4 md:ml-64 h-auto pt-20">
        <div className="sticky top-14 z-10 bg-gray-50 dark:bg-gray-900">
          <Breadcrumb />
        </div>
        {templatesStore.pageLoading ? (
          <SkeletonLoader />
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {/* Template Details Card */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-1">
                <h1 className="text-2xl font-semibold text-gray-800 dark:text-gray-200">
                  {templatesStore.selectedTemplate?.name}
                </h1>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => setEditMetaModalOpen(true)}
                    className="text-gray-500 hover:text-purple-600 focus:outline-none"
                  >
                    <AiOutlineEdit className="text-xl" />
                  </button>
                  <button
                    onClick={handleCloneTemplate}
                    className="text-gray-500 hover:text-green-600 focus:outline-none"
                    title="Clone Template"
                  >
                    <AiOutlineCopy className="text-xl" />
                  </button>
                  <button
                    onClick={() => setIsDeleteTemplateModalOpen(true)}
                    className="text-gray-500 hover:text-red-600 focus:outline-none"
                  >
                    <AiOutlineDelete className="text-xl" />
                  </button>
                </div>
              </div>
              <p className="text-gray-600 dark:text-gray-300 mb-2">
                {templatesStore.selectedTemplate?.description}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Created on:{" "}
                {templatesStore.selectedTemplate?.createdAt &&
                  new Date(
                    templatesStore.selectedTemplate.createdAt._seconds * 1000
                  ).toLocaleDateString()}
                <br />
                Last updated:{" "}
                {timestampToTimeAgo(templatesStore.selectedTemplate?.updatedAt)}
              </p>
            </div>

            {/* Tasks List Card */}
            <section className="mb-6">
              <div className="flex items-center">
                <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                  Tasks
                </h2>
                <AiFillPlusCircle
                  onClick={() => handleCreateTaskClicked()}
                  className="ml-2 text-2xl text-purple-500 dark:text-gray-400 cursor-pointer hover:text-purple-600"
                />
              </div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
                {tasks && tasks.length > 0 ? (
                  tasks.map((task) => (
                    <TaskDetails
                      key={task.id}
                      task={task}
                      editTaskClicked={handleEditTaskClicked}
                      isTemplate
                      deleteTaskClicked={handleDeleteTaskClicked}
                    />
                  ))
                ) : (
                  <p className="text-gray-500 italic">No tasks available.</p>
                )}
              </div>
            </section>
          </div>
        )}
      </main>
    </div>
  );
};

export default ViewTemplate;
